import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { IntakeShell, Progress, BackButton, QuestionHeader, GlowCard } from '../components/intake/v2/IntakeUI';
import { SingleChoice, MultiChoice, TextQuestion, DateQuestion, InputQuestion, AccessQuestion, PhotosQuestion, ReviewQuestion, QuestionScreen, ContactQuestion } from '../components/intake/v2/Questions';
import { PATHWAYS, SERVICE_CARDS, SERVICE_ORDER, visibleQuestions, OPTION_LABEL } from '../components/intake/v2/pathways';
import { ErrorScreen } from '../components/intake/IntakeShell';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const clean = (d) => Object.fromEntries(Object.entries(d).filter(([, v]) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && !v.length)));

const pageVariants = (reduce) => ({
  enter: (dir) => reduce ? { opacity: 0 } : { x: dir > 0 ? '60%' : '-60%', rotateY: dir > 0 ? -12 : 12, opacity: 0 },
  center: { x: 0, rotateY: 0, opacity: 1, transition: { duration: reduce ? 0.2 : 0.42, ease: [0.22, 1, 0.36, 1] } },
  exit: (dir) => reduce ? { opacity: 0, transition: { duration: 0.15 } } : { x: dir > 0 ? '-45%' : '45%', rotateY: dir > 0 ? 10 : -10, opacity: 0, transition: { duration: 0.3, ease: 'easeIn' } },
});

export default function IntakePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const storeKey = `gg_intake2_${token.slice(0, 12)}`;
  const [phase, setPhase] = useState('loading'); // loading | error | form
  const [error, setError] = useState(null);
  const [services, setServices] = useState([]);
  const [data, setData] = useState({});
  const [photos, setPhotos] = useState([]);
  const [idx, setIdx] = useState(-1); // -1 = service selection
  const [dir, setDir] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [initialRequest, setInitialRequest] = useState(null);
  const submissionId = useRef(null);
  const dirty = useRef(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [r, s] = await Promise.all([axios.get(`${API_URL}/api/intake/${token}`), axios.get(`${API_URL}/api/services`).catch(() => ({ data: [] }))]);
        if (!alive) return;
        if (r.data.status === 'completed') { navigate('/intake/complete', { replace: true, state: { completed: true } }); return; }
        const svc = (s.data || []).filter(x => x.intake_pathway && x.is_active !== false);
        setServices(SERVICE_ORDER.map(p => svc.find(x => x.intake_pathway === p)).filter(Boolean).length ? SERVICE_ORDER.map(p => svc.find(x => x.intake_pathway === p)).filter(Boolean) : SERVICE_ORDER.map(p => ({ id: null, intake_pathway: p, name: SERVICE_CARDS[p].label })));
        let local = null;
        try { local = JSON.parse(localStorage.getItem(storeKey) || 'null'); } catch { /* ignore */ }
        const base = { ...(r.data.draft || {}), ...(local?.data || {}) };
        if (!base.address?.line1 && r.data.prefill?.address) base.address = r.data.prefill.address;
        const pre = r.data.prefill || {};
        ['contact_name', 'contact_phone', 'contact_email', 'service', 'property_type', 'facility_type'].forEach((k) => { if (!base[k] && pre[k]) base[k] = pre[k]; });
        if (!base.move_direction && pre.move_direction && base.service === 'move') base.move_direction = pre.move_direction;
        if (base.service && !base.service_id) { const m = svc.find(x => x.intake_pathway === base.service); if (m) base.service_id = m.id; }
        setInitialRequest(pre.initial_request || null);
        setData(base);
        setPhotos((r.data.photos || []).map(p => ({ id: p.id, url: `${API_URL}/api/intake/${token}/photos/${p.id}` })));
        submissionId.current = local?.submission_id || crypto.randomUUID();
        if (base.service) {
          if (local?.idx != null) setIdx(local.idx);
          else { const qs = visibleQuestions(base.service, base); const first = qs.findIndex(x => !x.optional && !['photos', 'review', 'text', 'input', 'time'].includes(x.type) && (x.type === 'access' ? !(base.someone_home && base.access_method) : base[x.key] == null || base[x.key] === '' || (Array.isArray(base[x.key]) && !base[x.key].length))); setIdx(first < 0 ? qs.length - 1 : first); }
        }
        setPhase('form');
      } catch (e) {
        if (!alive) return;
        const code = e.response?.status;
        setError(code === 410 ? { title: 'This link has expired', message: "For your security, intake links expire. Please ask Gleam Guru for a fresh link." }
          : code === 404 ? { title: "This link isn't valid", message: 'Double-check the link you received, or contact Gleam Guru for a new one.' }
            : { title: "We couldn't load your intake", message: 'Please check your connection and try again in a moment.' });
        setPhase('error');
      }
    })();
    return () => { alive = false; };
  }, [token, storeKey, navigate]);

  useEffect(() => { if (phase === 'form') localStorage.setItem(storeKey, JSON.stringify({ data, idx, submission_id: submissionId.current })); }, [data, idx, phase, storeKey]);

  const saveDraft = useCallback((d) => {
    if (!dirty.current) return;
    axios.put(`${API_URL}/api/intake/${token}/draft`, clean(d)).then(() => { dirty.current = false; }).catch(() => {});
  }, [token]);

  const pathway = data.service;
  const questions = useMemo(() => (pathway ? visibleQuestions(pathway, data) : []), [pathway, data]);
  const q = idx >= 0 ? questions[Math.min(idx, questions.length - 1)] : null;
  const stages = PATHWAYS[pathway]?.stages || [];
  const stageFraction = useMemo(() => {
    if (!q) return 0;
    const inStage = questions.filter(x => x.stage === q.stage);
    return (inStage.indexOf(q) + 1) / inStage.length;
  }, [q, questions]);

  const setField = (k, v) => { dirty.current = true; setData(d => ({ ...d, [k]: v })); };
  const go = (next, direction) => { setDir(direction); setIdx(next); window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' }); };
  const advance = (d = data) => { saveDraft(d); go(Math.min(idx + 1, questions.length - 1), 1); };
  const answer = (k, v) => { const d = { ...data, [k]: v }; dirty.current = true; setData(d); saveDraft(d); go(idx + 1, 1); };
  const back = () => go(idx - 1, -1);
  const goTo = (key) => { const i = questions.findIndex(x => x.key === key); if (i >= 0) go(i, -1); };

  const chooseService = (svc) => {
    const d = { ...data, service: svc.intake_pathway, service_id: svc.id || undefined };
    if (data.service && d.service !== data.service) {
      // Keep shared answers that are still valid; drop answers that only belong to the previous service.
      const next = Object.fromEntries((PATHWAYS[d.service]?.questions || []).map(x => [x.key, x]));
      (PATHWAYS[data.service]?.questions || []).forEach((x) => {
        const n = next[x.key];
        const valid = n && (!n.options || [].concat(d[x.key]).every(v => n.options.some(o => String(o.value) === String(v))));
        if (!valid) delete d[x.key];
      });
    }
    dirty.current = true; setData(d); saveDraft(d); go(0, 1);
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/intake/${token}/submit`, { submission_id: submissionId.current, data: clean(data) });
      localStorage.removeItem(storeKey);
      navigate('/intake/complete', { state: { completed: true } });
    } catch (e) {
      if (e.response?.status === 409) { navigate('/intake/complete', { state: { completed: true } }); return; }
      toast.error(e.response?.data?.detail || "We couldn't send your details. Your answers are saved — please try again.");
    } finally { setSubmitting(false); }
  };

  if (phase === 'loading') return <IntakeShell><div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div></IntakeShell>;
  if (phase === 'error') return <ErrorScreen {...error} />;

  const crumbs = q ? [SERVICE_CARDS[pathway]?.label, OPTION_LABEL(questions.find(x => x.key === 'property_type' || x.key === 'facility_type')?.options, data.property_type || data.facility_type), data.bedrooms != null && idx > questions.findIndex(x => x.key === 'bedrooms') ? `${data.bedrooms} Bedroom${data.bedrooms === 1 ? '' : 's'}` : null, data.occupancy && idx > questions.findIndex(x => x.key === 'occupancy') ? OPTION_LABEL(questions.find(x => x.key === 'occupancy')?.options, data.occupancy) : null].filter(Boolean).slice(0, 4) : [];

  const renderQuestion = () => {
    switch (q.type) {
      case 'photo': case 'minimal': case 'number': case 'binary': return <SingleChoice q={q} value={data[q.key]} onAnswer={answer} />;
      case 'multi': return <MultiChoice q={q} value={data[q.key] || []} onChange={setField} onContinue={() => advance()} />;
      case 'text': return <TextQuestion q={q} value={data[q.key] || ''} onChange={setField} onContinue={() => advance()} />;
      case 'input': return <InputQuestion q={q} value={data[q.key] || ''} onChange={setField} onContinue={() => advance()} />;
      case 'date': case 'time': return <DateQuestion q={q} value={data[q.key] || ''} onChange={setField} onContinue={() => advance()} />;
      case 'access': return <AccessQuestion data={data} onChange={setField} onContinue={() => advance()} />;
      case 'contact': return <ContactQuestion data={data} onChange={setField} onContinue={() => advance()} />;
      case 'photos': return <PhotosQuestion q={q} token={token} photos={photos} setPhotos={setPhotos} onContinue={() => advance()} />;
      case 'review': return <ReviewQuestion pathway={pathway} data={data} photos={photos} goTo={goTo} onSubmit={submit} submitting={submitting} initialRequest={initialRequest} />;
      default: return null;
    }
  };

  const variants = pageVariants(reduce);
  return (
    <IntakeShell progress={q ? <Progress stages={stages} stageIndex={q.stage} stageFraction={stageFraction} /> : null} footer={q ? <BackButton onClick={back} /> : null}>
      <div style={{ perspective: 1400 }}>
        <AnimatePresence mode="wait" custom={dir} initial={false}>
          <motion.div key={q ? q.key : 'service'} custom={dir} variants={variants} initial="enter" animate="center" exit="exit" style={{ transformOrigin: dir > 0 ? 'left center' : 'right center' }}>
            {!q ? (
              <div data-testid="screen-service">
                <QuestionHeader eyebrow="Tell Us About Your Space." title="What can we help with?" subtitle="Choose a service to get started." />
                <div className="grid grid-cols-2 gap-3 sm:gap-4" role="radiogroup" aria-label="Service">
                  {services.map(s => { const c = SERVICE_CARDS[s.intake_pathway]; return <GlowCard key={s.intake_pathway} variant="photo" img={c.img} Icon={c.icon} label={c.label} desc={c.desc} selected={data.service === s.intake_pathway} onSelect={() => chooseService(s)} testId={`opt-service-${s.intake_pathway}`} className="!aspect-[4/5] sm:!aspect-[4/3]" />; })}
                </div>
              </div>
            ) : (
              <QuestionScreen q={q} crumbs={crumbs}>{renderQuestion()}</QuestionScreen>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </IntakeShell>
  );
}
